import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';

type ContextType = {
  disconnect: () => void;
  isStickySearchBar: boolean;
  observe: (selector: string) => void;
};

const defaultContext: ContextType = {
  disconnect: () => {},
  isStickySearchBar: false,
  observe: () => {}
};

export const StickySearchContext = createContext<ContextType>(defaultContext);

const useIntersectionObserver = (callback: IntersectionObserverCallback) => {
  const [observer, setObserver] = useState<IntersectionObserver | null>(null);

  const observe = (selector: string) => {
    const el = document.querySelector(selector);
    if (observer && el) {
      observer.observe(el);
    }
  };

  const disconnect = () => {
    if (observer) observer.disconnect();
  };

  useEffect(() => {
    setObserver(new IntersectionObserver(callback, {}));
  }, [callback]);

  return { observe, disconnect };
};

export default function StickySearchProvider({ children }) {
  const [isStickySearchBar, setStickySearchBar] = useState(false);
  const handleObserver = useCallback(entries => {
    const [entry] = entries;
    const { isIntersecting } = entry;
    setStickySearchBar(!isIntersecting);
  }, []);
  const { observe, disconnect } = useIntersectionObserver(handleObserver);

  const value = useMemo(
    () => ({
      isStickySearchBar,
      observe,
      disconnect
    }),
    [disconnect, isStickySearchBar, observe]
  );

  return (
    <StickySearchContext.Provider value={value}>
      {children}
    </StickySearchContext.Provider>
  );
}
